import { useState } from "react";
import { Eyebrow } from "@/components/media/Sections";
import { FLAVORS } from "@/constants/flavors";
import { Star } from "lucide-react";

export const liveItems = (category) =>
  (category?.items || [])
    .filter((i) => i.published !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

/** Tabbed retail menu: lemonades, dirty sodas, dirty alanis. */
export const DrinkMenu = ({ menu }) => {
  const categories = menu?.categories || [];
  const [active, setActive] = useState(categories[0]?.id);
  if (!categories.length) return null;
  const current = categories.find((c) => c.id === active) || categories[0];
  const items = liveItems(current);

  return (
    <>
      <Eyebrow>THE MENU</Eyebrow>
      <h2 className="neon mt-3 max-w-3xl text-4xl leading-tight sm:text-5xl">{menu.title}</h2>
      <p className="mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">{menu.intro}</p>

      <div className="mt-10 flex flex-wrap gap-2" role="tablist">
        {categories.map((c) => (
          <button
            key={c.id}
            role="tab"
            aria-selected={c.id === current.id}
            data-testid={`menu-tab-${c.id}`}
            onClick={() => setActive(c.id)}
            className={`mono rounded-sm border px-4 py-3 text-[11px] tracking-widest transition-colors ${
              c.id === current.id
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground hover:border-primary hover:text-primary"
            }`}
          >
            {(c.tab || c.name).toUpperCase()}
          </button>
        ))}
      </div>

      <p className="mt-6 max-w-2xl text-sm text-muted-foreground">{current.blurb}</p>

      <div
        data-testid={`menu-items-${current.id}`}
        className="mt-8 grid gap-4 sm:grid-cols-2"
      >
        {items.map((item, i) => (
          <div
            key={item.id}
            data-testid={`menu-item-${item.id}`}
            className="group relative flex gap-4 border border-border bg-[#141414] p-5 transition-colors hover:border-primary/70"
          >
            {item.image ? (
              <img
                src={item.image}
                alt=""
                loading="lazy"
                className="h-20 w-20 shrink-0 border border-border object-cover"
              />
            ) : (
              <span
                className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full transition-transform group-hover:scale-150"
                style={{ background: FLAVORS[i % FLAVORS.length].color }}
              />
            )}
            <div className="min-w-0">
              <div className="display text-xl uppercase leading-none text-white md:text-2xl">
                {item.name}
              </div>
              {item.badge ? (
                <span className="mono mt-2 inline-flex items-center gap-1 bg-primary px-2 py-0.5 text-[9px] tracking-widest text-primary-foreground">
                  <Star size={9} fill="currentColor" /> {item.badge}
                </span>
              ) : null}
              {item.build ? (
                <div className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {item.build}
                </div>
              ) : null}
              <div className="mono mt-3 text-[10px] tracking-widest text-primary">
                {(current.size || "24 OZ").toUpperCase()}
              </div>
              {item.available === false ? (
                <div
                  data-testid={`menu-item-unavailable-${item.id}`}
                  className="mono mt-2 text-[10px] tracking-widest text-muted-foreground"
                >
                  TEMPORARILY UNAVAILABLE
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {current.notice ? (
        <p
          data-testid={`menu-notice-${current.id}`}
          className="mono mt-6 max-w-2xl border-l-2 border-primary pl-4 text-[11px] leading-relaxed tracking-widest text-muted-foreground"
        >
          {current.notice}
        </p>
      ) : null}

      {menu.note ? (
        <p
          data-testid="menu-note"
          className="mono mt-8 max-w-2xl text-[11px] leading-relaxed tracking-widest text-muted-foreground"
        >
          {menu.note}
        </p>
      ) : null}

      {menu.cta_title ? (
        <div
          data-testid="menu-catering-cta"
          className="mt-14 border border-primary/60 bg-[#141414] p-7 md:p-10"
        >
          <h3 className="display text-2xl uppercase leading-none text-primary md:text-3xl">
            {menu.cta_title}
          </h3>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground">{menu.cta_body}</p>
          <a
            href={menu.cta_href || "#setups"}
            data-testid="menu-catering-cta-link"
            className="display mt-6 inline-flex rounded-sm bg-primary px-6 py-3 text-lg uppercase text-primary-foreground transition-colors hover:bg-[#E6C800]"
          >
            {menu.cta_label}
          </a>
        </div>
      ) : null}
    </>
  );
};
